import api from '@/lib/api';
import React from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Magic link consumer. The emailed link points here with token/email plus the
 * OAuth context (clientId/redirectUri/state) it was issued with. We exchange
 * the token at /auth/magic and follow the same urlToRedirect handoff the
 * password flow uses; without client context we just report success.
 */
export default function MagicLogin() {
	const [searchParams] = useSearchParams();
	const [status, setStatus] = React.useState<'verifying' | 'success' | 'error'>(
		'verifying'
	);
	const [message, setMessage] = React.useState('');
	// Tokens are single-use and consumed server-side, so the exchange must
	// fire exactly once — React.StrictMode double-fires effects in dev.
	const exchanged = React.useRef(false);

	React.useEffect(() => {
		const token = searchParams.get('token');
		const email = searchParams.get('email');
		const clientId = searchParams.get('clientId');
		const redirectUri = searchParams.get('redirectUri');
		const state = searchParams.get('state');

		if (!token || !email) {
			setStatus('error');
			setMessage('This magic link is malformed.');
			return;
		}

		if (exchanged.current) return;
		exchanged.current = true;

		let cancelled = false;
		(async () => {
			try {
				const response = await api.post(
					'/auth/magic',
					{ token, email },
					{
						// No retries: a retried POST would race the token's
						// single-use consumption.
						['axios-retry']: { retries: 0 },
						params: {
							clientId,
							redirectUri,
							...(state && { state }),
						},
					}
				);
				if (cancelled) return;
				if (response.data.urlToRedirect) {
					window.location.href = response.data.urlToRedirect;
					return;
				}
				setStatus('success');
			} catch (err: any) {
				if (cancelled) return;
				setStatus('error');
				setMessage(
					err?.response?.data?.message ||
						'This magic link is invalid or has expired.'
				);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [searchParams]);

	const signinUrl = `/signin${
		searchParams.get('appName')
			? `?appName=${encodeURIComponent(searchParams.get('appName')!)}`
			: ''
	}`;

	return (
		<div className='flex min-h-screen items-center justify-center bg-background px-4 py-12'>
			<div className='w-full max-w-md rounded-xl border border-border/50 bg-card p-8 text-center shadow-card'>
				{status === 'verifying' ? (
					<>
						<svg
							className='mx-auto h-10 w-10 animate-spin text-muted-foreground'
							xmlns='http://www.w3.org/2000/svg'
							fill='none'
							viewBox='0 0 24 24'
							aria-hidden='true'
						>
							<circle
								className='opacity-25'
								cx='12'
								cy='12'
								r='10'
								stroke='currentColor'
								strokeWidth='4'
							/>
							<path
								className='opacity-75'
								fill='currentColor'
								d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
							/>
						</svg>
						<h1 className='mt-4 text-lg font-semibold text-foreground'>
							Verifying your magic link…
						</h1>
					</>
				) : status === 'success' ? (
					<>
						<h1 className='text-lg font-semibold text-foreground'>
							You&apos;re logged in
						</h1>
						<p className='mt-2 text-sm text-muted-foreground'>
							You can close this tab and return to the app.
						</p>
					</>
				) : (
					<>
						<h1 className='text-lg font-semibold text-foreground'>
							Magic link failed
						</h1>
						<p className='mt-2 text-sm text-muted-foreground'>{message}</p>
						<a
							href={signinUrl}
							className='mt-6 inline-block text-sm font-medium text-[#4761d4] underline-offset-4 hover:underline'
						>
							Back to sign in
						</a>
					</>
				)}
			</div>
		</div>
	);
}
